import { Game } from "./game";
import { GAME_WIDTH, GAME_HEIGHT } from "./constants";
import { Keyboard } from "./keyboard";
import { Mouse } from "./mouse";
import FontFaceObserver from "fontfaceobserver";
import * as PIXI from "pixi.js";
import { SoundManager } from "./soundManager";

export class GameManager {
	private static gameContainer: HTMLElement | null;
	private static app: PIXI.Application;
	private static loadProgressText: PIXI.Text;

	public static init() {
		// Debug PIXI
		let type = "WebGL";

		if (!PIXI.utils.isWebGLSupported()) {
			type = "canvas";
		}
		PIXI.utils.sayHello(type);

		GameManager.gameContainer = document.getElementById("game-container");
		if (GameManager.gameContainer === null) {
			GameManager.gameContainer = document.body;
		}

		GameManager.app = new PIXI.Application({
			width: GAME_WIDTH,
			height: GAME_HEIGHT,
			antialias: true,
			transparent: true,
			backgroundColor: 0xececec,
		});

		// Init input
		Keyboard.init();
		Mouse.init();

		// Init sound
		SoundManager.init();

		// Add to the front
		GameManager.gameContainer.prepend(GameManager.app.view);

		const font = new FontFaceObserver("MrBubbleFont");

		// Add the loader to container
		font.load().then(() => {
			GameManager.loadProgressText = new PIXI.Text(
				"0%",
				new PIXI.TextStyle({
					fontFamily: "MrBubbleFont",
					fontSize: 128,
					fontWeight: "bold",
					align: "center",
				})
			);
			GameManager.loadProgressText.anchor.set(0.5);
			GameManager.loadProgressText.position.set(
				GAME_WIDTH / 2,
				GAME_HEIGHT / 2
			);
			GameManager.app.stage.addChild(GameManager.loadProgressText);

			// Load the sprites
			PIXI.Loader.shared
				.add("img/player/p_right.png")
				.add("img/platform.png")
				.add("img/player/p_gun.png")
				.add("img/player/p_bullet.png")
				.add("img/enemy.png")
				.add("audio/hover.wav")
				.add("audio/select.wav")
				.add("audio/hit.wav")
				.add("audio/shoot.wav")
				.add("audio/jump.wav")
				.add("audio/die.wav")
				.add("audio/fall.wav")
				.add("audio/happy.wav")
				.on("progress", GameManager.loadProgressHandler)
				.load(() => {
					SoundManager.init();
					GameManager.mainMenu();
				});
		});
	}

	private static loadProgressHandler(
		loader: PIXI.Loader,
		resource: PIXI.LoaderResource
	): void {
		GameManager.loadProgressText.text = `${Math.round(loader.progress)}%`;
	}

	public static mainMenu(): void {
		SoundManager.happy();

		GameManager.app.stage.removeChildren();

		const container = new PIXI.Container();
		const ticker = new PIXI.Ticker();

		const titleText = new PIXI.Text(
			"UP & UP",
			new PIXI.TextStyle({
				fontFamily: "MrBubbleFont",
				fontSize: 128,
				fontWeight: "bold",
				align: "center",
			})
		);
		titleText.anchor.set(0.5);
		titleText.position.set(GAME_WIDTH / 2, GAME_HEIGHT / 4);
		container.addChild(titleText);

		const startText = new PIXI.Text(
			"Start",
			new PIXI.TextStyle({
				fontFamily: "MrBubbleFont",
				fontSize: 72,
				fontWeight: "bold",
				align: "center",
			})
		);
		startText.anchor.set(0.5);
		startText.position.set(GAME_WIDTH / 2, GAME_HEIGHT / 2);
		startText.interactive = true;
		startText.buttonMode = true;
		startText.on("pointerover", () => {
			startText.style.fontSize = 92;
			SoundManager.hover();
		});
		startText.on("pointerout", () => {
			startText.style.fontSize = 72;
		});
		startText.on("pointerdown", () => {
			GameManager.newGame();
			SoundManager.click();
		});
		container.addChild(startText);

		GameManager.app.stage.addChild(container);

		const oldTicker = GameManager.app.ticker;
		GameManager.app.ticker = ticker;
		GameManager.app.ticker.start();
		oldTicker.stop();
	}

	public static newGame(): void {
		// Remove the previous game
		GameManager.app.stage.removeChildren();

		const container = new PIXI.Container();
		const ticker = new PIXI.Ticker();
		const mouse = GameManager.app.renderer.plugins.interaction.mouse.global;

		Game.init(container, ticker, mouse);
		GameManager.app.stage.addChild(container);

		const oldTicker = GameManager.app.ticker;
		GameManager.app.ticker = ticker;
		GameManager.app.ticker.start();
		oldTicker.stop();
	}
}
